import React, { useEffect, useState, useCallback } from 'react';

// Check if it's Republic Day period (Jan 17-27)
export const isRepublicDayPeriod = () => {
  const today = new Date();
  const month = today.getMonth(); // 0 = January
  const day = today.getDate();
  return month === 0 && day >= 17 && day <= 27;
};

// Spectacular Fireworks Effect
export const Fireworks = () => {
  const [fireworks, setFireworks] = useState([]);

  const createFirework = useCallback(() => {
    const colors = ['#FF9933', '#FFFFFF', '#138808', '#FFD700', '#FF6B35'];
    const x = 10 + Math.random() * 80;
    const y = 20 + Math.random() * 40;
    
    const particles = [];
    for (let i = 0; i < 20; i++) {
      particles.push({
        id: i,
        angle: (i * 18) * (Math.PI / 180),
        color: colors[Math.floor(Math.random() * colors.length)],
        distance: 30 + Math.random() * 50,
        delay: Math.random() * 0.3
      });
    }

    return {
      id: Date.now() + Math.random(),
      x,
      y,
      particles
    };
  }, []);

  useEffect(() => {
    if (!isRepublicDayPeriod()) return;

    const launchFirework = () => {
      setFireworks(prev => [...prev.slice(-3), createFirework()]);
    };

    launchFirework();
    const interval = setInterval(launchFirework, 2500);
    
    return () => clearInterval(interval);
  }, [createFirework]);

  if (!isRepublicDayPeriod()) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {fireworks.map((fw) => (
        <div
          key={fw.id}
          className="absolute"
          style={{ left: `${fw.x}%`, top: `${fw.y}%` }}
        >
          {fw.particles.map((p) => (
            <div
              key={p.id}
              className="absolute w-2 h-2 rounded-full animate-firework-particle"
              style={{
                backgroundColor: p.color,
                boxShadow: `0 0 6px ${p.color}, 0 0 12px ${p.color}`,
                '--angle': `${p.angle}rad`,
                '--distance': `${p.distance}px`,
                animationDelay: `${p.delay}s`
              }}
            />
          ))}
          <div className="absolute w-3 h-3 bg-white rounded-full animate-firework-center" 
               style={{ boxShadow: '0 0 20px #fff, 0 0 40px #FF9933' }} />
        </div>
      ))}
      <style>{`
        @keyframes firework-particle {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { 
            transform: translate(
              calc(cos(var(--angle)) * var(--distance)),
              calc(sin(var(--angle)) * var(--distance))
            ) scale(0);
            opacity: 0;
          }
        }
        @keyframes firework-center {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(3); opacity: 0; }
        }
        .animate-firework-particle {
          animation: firework-particle 1.5s ease-out forwards;
        }
        .animate-firework-center {
          animation: firework-center 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// Sparkle Rain Effect
export const SparkleRain = () => {
  const [sparkles, setSparkles] = useState([]);

  useEffect(() => {
    if (!isRepublicDayPeriod()) return;
    
    const colors = ['#FF9933', '#FFFFFF', '#138808', '#FFD700'];
    const newSparkles = [];
    
    for (let i = 0; i < 50; i++) {
      newSparkles.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 8,
        duration: 3 + Math.random() * 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 4 + Math.random() * 8
      });
    }
    setSparkles(newSparkles);
  }, []);

  if (!isRepublicDayPeriod()) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-40 overflow-hidden">
      {sparkles.map((s) => (
        <div
          key={s.id}
          className="absolute animate-sparkle-fall"
          style={{
            left: `${s.left}%`,
            top: '-20px',
            width: `${s.size}px`,
            height: `${s.size}px`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`
          }}
        >
          <svg viewBox="0 0 24 24" fill={s.color} className="w-full h-full">
            <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z"/>
          </svg>
        </div>
      ))}
      <style>{`
        @keyframes sparkle-fall {
          0% { transform: translateY(0) rotate(0deg) scale(0); opacity: 0; }
          10% { opacity: 1; transform: scale(1); }
          90% { opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg) scale(0.5); opacity: 0; }
        }
        .animate-sparkle-fall {
          animation: sparkle-fall ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Animated Ashoka Chakra with Glow
export const AshokaChakra = ({ size = 60 }) => {
  if (!isRepublicDayPeriod()) return null;
  
  return (
    <div 
      className="inline-block animate-chakra-spin"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <filter id="chakraGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <linearGradient id="chakraGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1a237e"/>
            <stop offset="50%" stopColor="#000080"/>
            <stop offset="100%" stopColor="#0d47a1"/>
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="46" fill="none" stroke="url(#chakraGrad)" strokeWidth="4" filter="url(#chakraGlow)"/>
        <circle cx="50" cy="50" r="12" fill="url(#chakraGrad)" filter="url(#chakraGlow)"/>
        {[...Array(24)].map((_, i) => (
          <g key={i} transform={`rotate(${i * 15} 50 50)`}>
            <path d="M50 14 L52 38 L48 38 Z" fill="url(#chakraGrad)"/>
            <circle cx="50" cy="12" r="3" fill="#000080"/>
          </g>
        ))}
      </svg>
      <style>{`
        @keyframes chakra-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-chakra-spin {
          animation: chakra-spin 10s linear infinite;
        }
      `}</style>
    </div>
  );
};

// Animated Waving Flag - Flowing Wave Tricolor
const WavingFlag = ({ size = 'normal' }) => {
  const width = size === 'large' ? 100 : 80;
  const height = size === 'large' ? 65 : 52;
  
  return (
    <div style={{ display: 'inline-block', width, height }}>
      <svg viewBox="0 0 120 80" style={{ width: '100%', height: '100%' }}>
        <defs>
          {/* Saffron gradient */}
          <linearGradient id="saffronWave" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FF9933" stopOpacity="0.9"/>
            <stop offset="50%" stopColor="#FFB366" stopOpacity="0.7"/>
            <stop offset="100%" stopColor="#FF9933" stopOpacity="0.9"/>
          </linearGradient>
          
          {/* Green gradient */}
          <linearGradient id="greenWave" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#138808" stopOpacity="0.9"/>
            <stop offset="50%" stopColor="#2ECC71" stopOpacity="0.7"/>
            <stop offset="100%" stopColor="#138808" stopOpacity="0.9"/>
          </linearGradient>
          
          {/* Lighter saffron */}
          <linearGradient id="saffronLight" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FFB366" stopOpacity="0.6"/>
            <stop offset="100%" stopColor="#FFCC99" stopOpacity="0.4"/>
          </linearGradient>
          
          {/* Lighter green */}
          <linearGradient id="greenLight" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2ECC71" stopOpacity="0.6"/>
            <stop offset="100%" stopColor="#82E0AA" stopOpacity="0.4"/>
          </linearGradient>
        </defs>
        
        {/* Background flowing waves - Saffron */}
        <path fill="url(#saffronLight)" opacity="0.7">
          <animate 
            attributeName="d" 
            dur="3s" 
            repeatCount="indefinite"
            values="
              M0,25 Q30,15 60,25 T120,25 L120,0 L0,0 Z;
              M0,20 Q30,30 60,20 T120,20 L120,0 L0,0 Z;
              M0,25 Q30,15 60,25 T120,25 L120,0 L0,0 Z
            "
          />
        </path>
        
        {/* Main Saffron wave */}
        <path fill="url(#saffronWave)">
          <animate 
            attributeName="d" 
            dur="2s" 
            repeatCount="indefinite"
            values="
              M0,30 Q20,20 40,30 Q60,40 80,30 Q100,20 120,30 L120,10 Q100,0 80,10 Q60,20 40,10 Q20,0 0,10 Z;
              M0,30 Q20,40 40,30 Q60,20 80,30 Q100,40 120,30 L120,10 Q100,20 80,10 Q60,0 40,10 Q20,20 0,10 Z;
              M0,30 Q20,20 40,30 Q60,40 80,30 Q100,20 120,30 L120,10 Q100,0 80,10 Q60,20 40,10 Q20,0 0,10 Z
            "
          />
        </path>
        
        {/* White/center area with Chakra */}
        <ellipse cx="60" cy="40" rx="14" ry="14" fill="white" stroke="#000080" strokeWidth="1.5">
          <animate attributeName="cy" values="40;42;40;38;40" dur="2s" repeatCount="indefinite"/>
        </ellipse>
        
        {/* Ashoka Chakra */}
        <g>
          <animateTransform attributeName="transform" type="translate" values="0,0;0,2;0,0;0,-2;0,0" dur="2s" repeatCount="indefinite"/>
          <circle cx="60" cy="40" r="11" fill="none" stroke="#000080" strokeWidth="1.5"/>
          {[...Array(24)].map((_, i) => (
            <line 
              key={i} 
              x1="60" 
              y1="31" 
              x2="60" 
              y2="36" 
              stroke="#000080" 
              strokeWidth="1"
              transform={`rotate(${i * 15} 60 40)`}
            />
          ))}
          <circle cx="60" cy="40" r="3" fill="#000080"/>
        </g>
        
        {/* Main Green wave */}
        <path fill="url(#greenWave)">
          <animate 
            attributeName="d" 
            dur="2s" 
            repeatCount="indefinite"
            values="
              M0,70 Q20,60 40,70 Q60,80 80,70 Q100,60 120,70 L120,50 Q100,60 80,50 Q60,40 40,50 Q20,60 0,50 Z;
              M0,70 Q20,80 40,70 Q60,60 80,70 Q100,80 120,70 L120,50 Q100,40 80,50 Q60,60 40,50 Q20,40 0,50 Z;
              M0,70 Q20,60 40,70 Q60,80 80,70 Q100,60 120,70 L120,50 Q100,60 80,50 Q60,40 40,50 Q20,60 0,50 Z
            "
          />
        </path>
        
        {/* Background flowing waves - Green */}
        <path fill="url(#greenLight)" opacity="0.7">
          <animate 
            attributeName="d" 
            dur="3s" 
            repeatCount="indefinite"
            values="
              M0,80 L120,80 L120,55 Q90,65 60,55 T0,55 Z;
              M0,80 L120,80 L120,60 Q90,50 60,60 T0,60 Z;
              M0,80 L120,80 L120,55 Q90,65 60,55 T0,55 Z
            "
          />
        </path>
      </svg>
    </div>
  );
};

// Rotating Patriotic Quotes
const patrioticQuotes = [
  { text: "Jai Hind!", hindi: "जय हिंद!" },
  { text: "Vande Mataram", hindi: "वंदे मातरम्" },
  { text: "Unity in Diversity", hindi: "अनेकता में एकता" },
  { text: "Satyameva Jayate", hindi: "सत्यमेव जयते" },
  { text: "Proud to be Indian", hindi: "भारतीय होने पर गर्व" }
];

// Glassmorphism Republic Day Banner
export const RepublicDayBanner = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [quoteIndex, setQuoteIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % patrioticQuotes.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  if (!isRepublicDayPeriod() || !isVisible) return null;

  return (
    <div className="relative overflow-hidden">
      {/* Animated Aurora Background */}
      <div className="absolute inset-0 aurora-bg" />
      
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 backdrop-blur-sm bg-white/10" />
      
      {/* Moving light beams */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-40 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-light-sweep" />
        <div className="absolute w-20 h-full bg-gradient-to-r from-transparent via-[#FF9933]/30 to-transparent -skew-x-12 animate-light-sweep-delayed" />
      </div>

      <div className="relative py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-4">
          {/* Left Flag */}
          <div className="flex-shrink-0 hidden sm:block">
            <WavingFlag />
          </div>

          {/* Center Content */}
          <div className="text-center flex-1">
            <div className="flex items-center justify-center gap-3 mb-1">
              <AshokaChakra size={30} />
              <div>
                <h2 className="text-lg md:text-xl font-black tracking-wide">
                  <span className="text-[#FF9933] drop-shadow-lg">77th </span>
                  <span className="text-white drop-shadow-lg">Republic </span>
                  <span className="text-[#138808] drop-shadow-lg">Day</span>
                </h2>
                <p className="text-xs text-white/80 font-medium tracking-widest">
                  26 JANUARY 2026
                </p>
              </div>
              <AshokaChakra size={30} />
            </div>
            
            {/* Animated Quote */}
            <div className="h-6 overflow-hidden">
              <div 
                className="transition-all duration-700 ease-out"
                style={{ transform: `translateY(-${quoteIndex * 24}px)` }}
              >
                {patrioticQuotes.map((q, i) => (
                  <p key={i} className="h-6 flex items-center justify-center gap-2 text-xs md:text-sm font-semibold">
                    <span className="text-[#FFD700]">{q.text}</span>
                    <span className="text-white/60">|</span>
                    <span className="text-white/90">{q.hindi}</span>
                  </p>
                ))}
              </div>
            </div>
          </div>

          {/* Right Flag */}
          <div className="flex-shrink-0 hidden md:block">
            <WavingFlag />
          </div>

          {/* Close Button */}
          <button
            onClick={() => {
              setIsVisible(false);
              if (onClose) onClose();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/40 text-white p-2 rounded-full transition-all duration-300 hover:scale-110 hover:rotate-90 border border-white/30"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <style>{`
        .aurora-bg {
          background: linear-gradient(
            135deg,
            #FF9933 0%,
            #FF6B35 15%,
            #FFFFFF 30%,
            #E8E8E8 45%,
            #138808 60%,
            #0D5C05 75%,
            #FF9933 90%,
            #FF6B35 100%
          );
          background-size: 400% 400%;
          animation: aurora 8s ease infinite;
        }
        @keyframes aurora {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes light-sweep {
          0% { left: -20%; }
          100% { left: 120%; }
        }
        @keyframes light-sweep-delayed {
          0% { left: -30%; }
          100% { left: 130%; }
        }
        .animate-light-sweep {
          animation: light-sweep 4s ease-in-out infinite;
        }
        .animate-light-sweep-delayed {
          animation: light-sweep-delayed 4s ease-in-out infinite;
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

// Animated Wave Border
export const TricolorNavbarBorder = () => {
  if (!isRepublicDayPeriod()) return null;
  
  return (
    <div className="h-2 w-full relative overflow-hidden">
      <div className="absolute inset-0 flex">
        <div className="flex-1 bg-[#FF9933] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer-fast" />
        </div>
        <div className="flex-1 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#000080]/20 to-transparent animate-shimmer-fast" style={{animationDelay: '0.3s'}} />
        </div>
        <div className="flex-1 bg-[#138808] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer-fast" style={{animationDelay: '0.6s'}} />
        </div>
      </div>
      <style>{`
        @keyframes shimmer-fast {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer-fast {
          animation: shimmer-fast 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Floating Diyas (Oil Lamps)
export const FloatingDiyas = () => {
  if (!isRepublicDayPeriod()) return null;

  const diyas = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    left: 5 + i * 12,
    delay: i * 0.5,
    duration: 15 + Math.random() * 10
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-30 overflow-hidden">
      {diyas.map((d) => (
        <div
          key={d.id}
          className="absolute animate-float-diya"
          style={{
            left: `${d.left}%`,
            bottom: '-60px',
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.duration}s`
          }}
        >
          {/* Diya container */}
          <div className="relative">
            {/* Flame */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="w-3 h-5 bg-gradient-to-t from-[#FF9933] via-[#FFD700] to-white rounded-full animate-flame-flicker opacity-90"
                   style={{ boxShadow: '0 0 20px #FFD700, 0 0 40px #FF9933' }} />
            </div>
            {/* Lamp base */}
            <div className="w-10 h-4 bg-gradient-to-b from-[#CD7F32] to-[#8B4513] rounded-full" />
            <div className="w-8 h-2 mx-auto bg-gradient-to-b from-[#8B4513] to-[#654321] rounded-b-full" />
          </div>
        </div>
      ))}
      <style>{`
        @keyframes float-diya {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-120vh) rotate(20deg); opacity: 0; }
        }
        @keyframes flame-flicker {
          0%, 100% { transform: scaleY(1) scaleX(1); }
          25% { transform: scaleY(1.1) scaleX(0.9); }
          50% { transform: scaleY(0.9) scaleX(1.1); }
          75% { transform: scaleY(1.15) scaleX(0.85); }
        }
        .animate-float-diya {
          animation: float-diya linear infinite;
        }
        .animate-flame-flicker {
          animation: flame-flicker 0.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Patriotic Corner Accents
export const CornerAccents = () => {
  if (!isRepublicDayPeriod()) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-20">
      {/* Top Left */}
      <div className="absolute top-0 left-0 w-32 h-32">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#FF9933] to-transparent" />
        <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-[#FF9933] to-transparent" />
        <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 border-[#FF9933]/30 rounded-tl-xl" />
      </div>
      
      {/* Top Right */}
      <div className="absolute top-0 right-0 w-32 h-32">
        <div className="absolute top-0 right-0 w-full h-2 bg-gradient-to-l from-[#138808] to-transparent" />
        <div className="absolute top-0 right-0 w-2 h-full bg-gradient-to-b from-[#138808] to-transparent" />
        <div className="absolute top-4 right-4 w-16 h-16 border-r-4 border-t-4 border-[#138808]/30 rounded-tr-xl" />
      </div>
      
      {/* Bottom Left */}
      <div className="absolute bottom-0 left-0 w-32 h-32">
        <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-r from-[#138808] to-transparent" />
        <div className="absolute bottom-0 left-0 w-2 h-full bg-gradient-to-t from-[#138808] to-transparent" />
      </div>
      
      {/* Bottom Right */}
      <div className="absolute bottom-0 right-0 w-32 h-32">
        <div className="absolute bottom-0 right-0 w-full h-2 bg-gradient-to-l from-[#FF9933] to-transparent" />
        <div className="absolute bottom-0 right-0 w-2 h-full bg-gradient-to-t from-[#FF9933] to-transparent" />
      </div>
    </div>
  );
};

// Patriotic Background with Animated Elements
export const PatrioticBackground = ({ children }) => {
  if (!isRepublicDayPeriod()) return <>{children}</>;

  return (
    <div className="relative min-h-screen">
      {/* Animated gradient corners */}
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: `
            radial-gradient(ellipse at top left, rgba(255,153,51,0.15) 0%, transparent 50%),
            radial-gradient(ellipse at top right, rgba(19,136,8,0.15) 0%, transparent 50%),
            radial-gradient(ellipse at bottom left, rgba(19,136,8,0.1) 0%, transparent 40%),
            radial-gradient(ellipse at bottom right, rgba(255,153,51,0.1) 0%, transparent 40%)
          `
        }}
      />
      
      {/* Animated Chakra watermark */}
      <div className="fixed bottom-20 right-20 opacity-[0.04] pointer-events-none z-0 animate-slow-spin">
        <svg viewBox="0 0 100 100" className="w-80 h-80">
          <circle cx="50" cy="50" r="48" fill="none" stroke="#000080" strokeWidth="2"/>
          <circle cx="50" cy="50" r="10" fill="#000080"/>
          {[...Array(24)].map((_, i) => (
            <g key={i} transform={`rotate(${i * 15} 50 50)`}>
              <path d="M50 8 L52 40 L48 40 Z" fill="#000080"/>
            </g>
          ))}
        </svg>
      </div>

      <CornerAccents />

      <div className="relative z-10">
        {children}
      </div>

      <style>{`
        @keyframes slow-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-slow-spin {
          animation: slow-spin 60s linear infinite;
        }
      `}</style>
    </div>
  );
};

// Main Theme Wrapper Component
const RepublicDayTheme = ({ children, showBanner = true, showEffects = true }) => {
  const [bannerClosed, setBannerClosed] = useState(false);

  if (!isRepublicDayPeriod()) return <>{children}</>;

  return (
    <PatrioticBackground>
      {showEffects && (
        <>
          <Fireworks />
          <SparkleRain />
        </>
      )}
      <TricolorNavbarBorder />
      {showBanner && !bannerClosed && (
        <RepublicDayBanner onClose={() => setBannerClosed(true)} />
      )}
      {children}
    </PatrioticBackground>
  );
};

export default RepublicDayTheme;
